import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { formatNumber } from '@progress/kendo-intl';
import { fetchApi } from '../../services/api';
import { CompositeFilterDescriptor, DataSourceRequestState, State as GridState, toDataSourceRequestString } from '@progress/kendo-data-query';
import { Grid, GridColumn as Column, GridCustomCellProps, GridDataStateChangeEvent, GridEvent, GridFilterChangeEvent, GridHandle, GridSortChangeEvent, GridToolbar } from '@progress/kendo-react-grid';
import { Title } from '../../utils/title';
import { ILink } from '../../types/link';
import './style.css';
import { BooleanFilter, Filter, FilterChangeEvent, Operators, TextFilter } from '@progress/kendo-react-data-tools';
import OwnerNumberCell from './OwnerNumberCell';
import { useDebouncedCallback } from 'use-debounce';
import { Button } from '@progress/kendo-react-buttons';
import AddOwnerDialog from '../AssetOwners/AddOwnerDialog';
import { plusIcon } from '@progress/kendo-svg-icons';

export type OwnerViewModel = {
    OwnerID: number;
    OwnerNumber: string;
    OwnerFullName: string;
    FirstName: string;
    LastName: string;
    CompanyName: string;
    VendorID: string;
    EMailAddress: string;
    Active: boolean;
    FederalTaxpayerID: string;
    Hash: string;
    Links: ILink[];
}

const PageSize = 50;

const OwnerGrid = () => {
    const resetDataState = {
        skip: 0,
        take: PageSize,
        sort: [{
            field: "CompanyName",
            dir: "asc"
        }]
    } as GridState;

    const defaultAndFilter = {
        logic: 'and', filters: [
            { field: 'active', operator: 'eq', value: true },
        ]
    } as CompositeFilterDescriptor;

    const sessionStorageAndFilter = sessionStorage.getItem("OwnerGrid-andFilter");
    let andFilter: CompositeFilterDescriptor = null;
    if (sessionStorageAndFilter) {
        andFilter = JSON.parse(sessionStorageAndFilter);
    } else {
        andFilter = { ...defaultAndFilter };
    }
    const [dataState, setDataState] = useState<DataSourceRequestState>({ ...resetDataState, filter: andFilter });
    const [loading, setLoading] = useState(false);
    const [owners, setOwners] = useState<OwnerViewModel[]>([]);
    const [showAddOwnerDialog, setShowAddOwnerDialog] = useState(false);
    const gridRef = useRef<GridHandle>(null);
    const [totalRecords, setTotalRecords] = useState(0);

    useLayoutEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        }
    }, [])

    const fetchRecords = useCallback((append: boolean) => {
        sessionStorage.setItem("OwnerGrid-andFilter", JSON.stringify(dataState.filter));
        setLoading(true);
        const dataGridState = { ...dataState };
        if (!append) {
            dataGridState.take = PageSize;
            dataGridState.skip = 0;
            window.scrollTo(0, 0);
            gridRef.current?.element?.querySelector<HTMLElement>('.k-grid-content')?.scrollTo(0, 0);
        }
        const queryStr = `${toDataSourceRequestString(dataGridState)}`;
        fetchApi(`/api/Asset/GetAssetOwners?${queryStr}`, {}, 'POST')
            .then(({ Data, Total }) => {
                setTotalRecords(Total);
                if (!append) {
                    setOwners(Data);
                } else {
                    setOwners(prevData => prevData.concat(Data));
                }
                setLoading(false);
                handleResize(null);
            }).catch(() => {
                setLoading(false);
            });
    }, [dataState]);

    const handleResize = (e: UIEvent) => {
        if ((document.getElementsByClassName("k-grid")[0]) as HTMLDivElement != null) {
            ((document.getElementsByClassName("k-grid")[0]) as HTMLDivElement).style.height = (document.documentElement.clientHeight - 250) + "px";
        }
    }

    const scrollHandler = (event: GridEvent) => {
        const scrollElement = event.syntheticEvent.currentTarget as HTMLElement;
        const isNearBottom = scrollElement.scrollHeight > scrollElement.clientHeight
            && scrollElement.scrollTop + scrollElement.clientHeight >= scrollElement.scrollHeight - 10;

        if (!isNearBottom || loading || owners.length >= totalRecords)
            return;

        dataState.skip = owners.length;
        fetchRecords(true);
    }

    const OwnerEmailAddressCell = (props: GridCustomCellProps) => {
        if (!props.field)
            return null;

        const dataItem: OwnerViewModel = props.dataItem;
        //props.dataItem[props.field]
        return (
            <td {...props.tdProps}>
                <a
                    href={"mailto:" + dataItem.EMailAddress}
                    style={{ color: '#007bff' }}
                >{dataItem.EMailAddress}
                </a>
            </td>
        );
    };

    const search = useDebouncedCallback(useCallback(() => {
        fetchRecords(false);
    }, [fetchRecords]), 750);

    useEffect(() => {
        search();
    }, [dataState.filter, dataState.sort, search]);

    const dataStateChange = (changeEvent: GridDataStateChangeEvent) => {
        setDataState(changeEvent.dataState);
    }

    const onFilterChange = (event: FilterChangeEvent) => {
        setDataState({ ...dataState, filter: event.filter });
    };

    const onGridFilterChange = (event: GridFilterChangeEvent) => {
        setDataState({ ...dataState, filter: event.filter ?? { ...defaultAndFilter } });
    };

    const onGridSortChange = (event: GridSortChangeEvent) => {
        setDataState({ ...dataState, sort: event.sort });
    };

    return <React.Fragment>
            <Title string="Owner Listing" />
            {showAddOwnerDialog && <AddOwnerDialog
                CloseDialog={() => setShowAddOwnerDialog(false)}
            />}
            <div>
                <Filter
                    className="kendoFilterControl"
                    value={dataState.filter}
                    onChange={onFilterChange}
                    fields={[
                        {
                            name: "OwnerNumber",
                            label: "Owner ID",
                            filter: TextFilter,
                            operators: Operators.text,
                        },
                        {
                            name: "OwnerFullName",
                            label: "Owner Name",
                            filter: TextFilter,
                            operators: Operators.text,
                        },
                        {
                            name: "active",
                            label: "Active",
                            filter: BooleanFilter,
                            operators: Operators.boolean
                        },
                        {
                            name: "CompanyName",
                            label: "Company Name",
                            filter: TextFilter,
                            operators: Operators.text,
                        },
                        {
                            name: "VendorID",
                            label: "Vendor ID",
                            filter: TextFilter,
                            operators: Operators.text,
                        },
                        {
                            name: "EMailAddress",
                            label: "EMail Address",
                            filter: TextFilter,
                            operators: Operators.text,
                        },
                        {
                            name: "FederalTaxpayerID",
                            label: "Fed. TaxPayer ID",
                            filter: TextFilter,
                            operators: Operators.text,

                        }
                    ]}
                />
            </div>
            <Grid
                ref={gridRef}
                {...dataState}
                scrollable="scrollable"
                onDataStateChange={dataStateChange}
                data={owners}
                resizable={true}
                reorderable={true}
                sortable={true}
                onScroll={scrollHandler}
                filterable={true}
                onFilterChange={(e) => onGridFilterChange(e)}
                onSortChange={(e) => onGridSortChange(e)}
                dataItemKey="OwnerID"
                showLoader={loading}
            >
                <GridToolbar>
                    <Button
                        icon="plus"
                        svgIcon={plusIcon}
                        themeColor="primary"
                        onClick={() => setShowAddOwnerDialog(true)}
                    > Owner
                    </Button>
                </GridToolbar>
                <Column field="OwnerNumber" title="Owner ID" cells={{ data: OwnerNumberCell }} />
                <Column field="OwnerFullName" title="Name" />
                <Column field="CompanyName" title="Company" />
                <Column field="VendorID" title="Vendor ID" media="(min-width: 768px)" />
                <Column field="EMailAddress" title="E-Mail" media="(min-width: 768px)" cells={{ data: OwnerEmailAddressCell }} />
            </Grid>
            {
                totalRecords > 0 &&
                <div className="k-pager k-pager-md k-grid-pagert">
                    <div style={{ marginLeft: "auto", marginRight: 0 }}>
                        {formatNumber(Math.min(owners.length, totalRecords), 'n0')} of {formatNumber(totalRecords, 'n0')} items
                    </div>
                </div>


            }
        </React.Fragment>
}

export default OwnerGrid;
